import { Injectable, inject } from '@angular/core';
import { getCapacitorPlatform } from 'environments/environment-helpers';
import { LoggingService } from 'services/logging.service';
import { TelemetryService } from 'services/telemetry.service';
import { DateTime } from 'luxon';
import {
  LastCountedBlock,
  ScannerName,
  isNewBlockedEpisode,
  shouldReportRepeatedCameraBlock,
} from 'services/camera-permission-help';

/** The surface wording Mixpanel's `app_platform` property is documented with:
 *  web covers the browser and the installed PWA alike. */
function describeAppPlatform(): 'web' | 'android-native' | 'ios-native' {
  switch (getCapacitorPlatform()) {
    case 'android': return 'android-native';
    case 'ios': return 'ios-native';
    default: return 'web';
  }
}

/**
 * Records that a camera screen had to tell the user their camera is blocked
 * (#5139): each blocked attempt as an analytics event, and the user going round
 * in circles as a Sentry problem.
 *
 * An attempt is not the same as the card being drawn — see
 * `isNewBlockedEpisode`. Why any of this is recorded, and what the rule costs,
 * is in `design-docs/scanner-shared-components.md`, "Where the camera
 * permission lives".
 */
@Injectable({ providedIn: 'root' })
export class CameraPermissionBlockService {
  private telemetry = inject(TelemetryService);
  private logging = inject(LoggingService);

  /** blocked episodes counted since the app started, across every camera screen */
  private showCount = 0;
  /** the last episode counted, so a card that is merely remounted is not counted again */
  private lastCounted: LastCountedBlock | undefined;
  /** which scanners were involved, so the Sentry report says whether the user
   *  was retrying one scanner or wandering between several */
  private scannersSeen = new Set<ScannerName>();

  /**
   * Call each time a camera screen shows the blocked-camera card.
   *
   * Safe to call on every display: showing the same card again straight away
   * is not a second attempt, and is ignored (`isNewBlockedEpisode`).
   */
  recordBlockedCardShown(scanner: ScannerName): void {
    const nowMillis = DateTime.utc().toMillis();
    // if this is the same card coming straight back, it is not a new attempt
    if (!isNewBlockedEpisode(this.lastCounted, scanner, nowMillis)) {
      return;
    }
    this.lastCounted = { scanner, atMillis: nowMillis };
    this.showCount++;
    this.scannersSeen.add(scanner);

    const appPlatform = describeAppPlatform();
    this.telemetry.trackEvent('camera_permission_blocked', { scanner, app_platform: appPlatform });

    // if the user keeps landing back on this card, the instructions are not
    // getting them anywhere and they need a look from us
    if (shouldReportRepeatedCameraBlock(this.showCount)) {
      this.logging.trackSilentProblem('camera-permission.repeatedly-blocked', {
        show_count: this.showCount,
        app_platform: appPlatform,
        scanners: Array.from(this.scannersSeen).join(','),
      });
    }
  }
}
